<template>
  <div
    ref="refi-calc-top-of-page"
    class="calc-main refinance sliders"
    data-name="Refinance calculator"
  >
    <div
      v-if="!isSubmitted"
      id="sliders-page"
    >
      <section class="calc-main__container">
        <form
          class="form_wrapper"
          action
        >
          <div class="block-header">
            <h3>Your existing mortgage information</h3>
          </div>
          <div class="row">
            <div
              class="col-12 col-md-6 input-fields"
            >
              <div
                id="refi-input"
                class="refi-input__wrapper"
              >

                <div class="existing-mortgage">
                  <div
                    id="first-month-mortgage-balance-input-group"
                    class="form-group"
                  >
                    <label for="first-month-mortgage-balance-input-field">First Monthly Mortgage Balance</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>This is the current principal balance or payoff amount.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.firstMortgageBalance.$model"
                        class="form-control"
                        aria-label="First Monthly Mortgage Balance"
                        :class="{
                          'is-invalid':
                            $v.firstMortgageBalance.$error || $v.firstMortgageBalance.$invalid
                        }"
                        @input="updateLtvRatio"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="gross-income-input-range"
                        v-model.number="firstMortgageBalance"
                        type="range"
                        aria-label="First Monthly Mortgage Balance Range"
                        class="custom-range"
                        min="1000"
                        max="5000000"
                        @input="updateLtvRatio"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="
                            !$v.firstMortgageBalance.between || !$v.firstMortgageBalance.required
                          "
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.firstMortgageBalance.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.firstMortgageBalance.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="est-home-value-input-group"
                    class="form-group"
                  >
                    <label for="est-home-value-input-field">Estimated Home Value</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>If you are unsure of the current appraised value of your home, consider what similar homes in your area are selling for, which may be helpful in determining current value.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.estHomeValue.$model"
                        class="form-control"
                        aria-label="Estimated Home Value"
                        :class="{
                          'is-invalid': $v.estHomeValue.$error || $v.estHomeValue.$invalid
                        }"
                        @input="updateLtvRatio"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="est-home-input-range"
                        v-model.number="estHomeValue"
                        type="range"
                        aria-label="Estimated Home Value Range"
                        class="custom-range"
                        min="1000"
                        max="5000000"
                        @input="updateLtvRatio"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="!$v.estHomeValue.between || !$v.estHomeValue.required"
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.estHomeValue.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.estHomeValue.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="loan-to-value-input-group"
                    class="form-group"
                  >
                    <label for="loan-to-value-input-field">Loan-to-Value (LTV) Ratio</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>Loan-to-Value is calculated for you by dividing the mortgage balance by the home's current market value.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <label style="font-size: 18px; font-weight: 700;">{{ ltvRatio }}%</label>
                    </div>
                  </div>
                  <div
                    id="other-debts-input-group"
                    class="form-group"
                  >
                    <label
                      for="other-debts-input-field"
                    >Monthly Payment &mdash; Principal & Interest Only
                      <span
                        class="tooltipContent"
                        data-toggle="tooltip"
                        data-placement="right"
                        data-html="true"
                        title="<p>If you are unsure of this amount, refer to a recent mortgage statement or call your mortgage company to find out.</p>"
                      >
                        <i class="fa fa-info-circle fa-1x" />
                      </span>
                    </label>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.monthlyPayment.$model"
                        class="form-control"
                        aria-label="Monthly Payment &mdash; Principal & Interest Only"
                        :class="{
                          'is-invalid':
                            $v.monthlyPayment.$error || $v.monthlyPayment.$invalid
                        }"
                        @input="updateTotalMonthlyPayment"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="monthly-payment-input-range"
                        v-model.number="monthlyPayment"
                        type="range"
                        aria-label="Monthly Payment &mdash; Principal & Interest Only Range"
                        class="custom-range"
                        min="1"
                        max="100000"
                        @input="updateTotalMonthlyPayment"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="
                            !$v.monthlyPayment.between || !$v.monthlyPayment.required
                          "
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.monthlyPayment.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.monthlyPayment.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="annual-property-tax-input-group"
                    class="form-group"
                  >
                    <label
                      for="annual-property-tax-input-field"
                    >Annual Property Taxes</label>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.propertyTaxes.$model"
                        class="form-control"
                        aria-label="Annual Property Taxes"
                        :class="{
                          'is-invalid':
                            $v.propertyTaxes.$error || $v.propertyTaxes.$invalid
                        }"
                        @input="updateTotalMonthlyPayment"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="property-taxes-input-range"
                        v-model.number="propertyTaxes"
                        type="range"
                        class="custom-range"
                        aria-label="Annual Property Taxes Range"
                        min="0"
                        max="1000000"
                        @input="updateTotalMonthlyPayment"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="
                            !$v.propertyTaxes.between || !$v.propertyTaxes.required
                          "
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.propertyTaxes.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.propertyTaxes.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="annual-property-insurance-input-group"
                    class="form-group"
                  >
                    <label
                      for="annual-property-insurance-input-field"
                    >Annual Property Insurance</label>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.propertyInsurance.$model"
                        class="form-control"
                        aria-label="Annual Property Insurance"
                        :class="{
                          'is-invalid':
                            $v.propertyInsurance.$error || $v.propertyInsurance.$invalid
                        }"
                        @input="updateTotalMonthlyPayment"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="property-insurance-input-range"
                        v-model.number="propertyInsurance"
                        type="range"
                        class="custom-range"
                        aria-label="Annual Property Insurance Range"
                        min="0"
                        max="1000000"
                        @input="updateTotalMonthlyPayment"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="
                            !$v.propertyInsurance.between || !$v.propertyInsurance.required
                          "
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.propertyInsurance.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.propertyInsurance.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="hoa-input-group"
                    class="form-group"
                  >
                    <label
                      for="hoa-input-field"
                    >Annual Homeowners Association (HOA) Fees</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>Enter your total HOA fee for the year, if applicable.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text">$</div>
                      </div>
                      <CurrencyComponent
                        v-model.number="$v.hoa.$model"
                        class="form-control"
                        aria-label="Annual Homeowners Association (HOA) Fees"
                        :class="{
                          'is-invalid': $v.hoa.$error || $v.hoa.$invalid
                        }"
                        @input="updateTotalMonthlyPayment"
                      />
                      <div class="input-group-append">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="hoa-input-range"
                        v-model.number="hoa"
                        type="range"
                        aria-label="Annual Homeowners Association (HOA) Fees Range"
                        class="custom-range"
                        min="0"
                        max="60000"
                        @input="updateTotalMonthlyPayment"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="!$v.hoa.between || !$v.hoa.required"
                        >{{ errorMsgPre }}
                          ${{
                            Number(
                              $v.hoa.$params.between.min
                            ).toLocaleString()
                          }}
                          and ${{
                            Number(
                              $v.hoa.$params.between.max
                            ).toLocaleString()
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="total-monthly-payment-group"
                    class="form-group"
                  >
                    <label
                      for="total-monthly-payment-field"
                    >Total Monthly Payment</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>This is the total monthly payment including principal, interest, taxes, insurance and HOA fees, if applicable.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <label style="font-size: 18px; font-weight: 700;">{{ currencyFormat(totalMonthlyPayment) }}</label>
                    </div>
                  </div>
                </div>

                <div class="block-header">
                  <h3>Refinancing information</h3>
                </div>
                <div class="refinancingInfo">
                  <div
                    id="term-input-group"
                    class="form-group"
                  >
                    <label for="term-input-select">New Term</label>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text" />
                      </div>
                      <select
                        id="term-input-select"
                        v-model.number="term"
                        class="form-control"
                        @input="sendDataLayer"
                      >
                        <option>10</option>
                        <option>15</option>
                        <option selected>20</option>
                        <option>30</option>
                        <option>40</option>
                        <option>50</option>
                      </select>
                      <div class="input-group-append">
                        <div class="input-group-text">yrs</div>
                      </div>
                      <input
                        id="term-input-range"
                        v-model.number="term"
                        type="range"
                        class="custom-range"
                        aria-label="New Term"
                        min="10"
                        max="50"
                        :step="termRangeStep"
                        @input="setSteps"
                        @change="sendDataLayer()"
                      >
                    </div>
                  </div>
                  <div
                    id="interest-rate-input-group"
                    class="form-group"
                  >
                    <label for="interest-rate-input-field">Interest Rate</label>
                    <div class="input-group mb-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text" />
                      </div>
                      <input
                        id="interest-rate-input-field"
                        v-model.number="$v.interestRatePercent.$model"
                        type="number"
                        step="10"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            $v.interestRatePercent.$error ||
                            $v.interestRatePercent.$invalid
                        }"
                        @blur="sendDataLayerOnBlur"
                      >
                      <div class="input-group-append">
                        <div class="input-group-text">%</div>
                      </div>
                      <input
                        id="interest-rate-input-range"
                        v-model.number="interestRatePercent"
                        type="range"
                        class="custom-range"
                        aria-label="Interest Rate"
                        min="0"
                        max="20"
                        step="0.25"
                        @change="sendDataLayer()"
                      >
                      <div class="invalid-feedback">
                        <span
                          v-if="
                            !$v.interestRatePercent.between ||
                              !$v.interestRatePercent.required
                          "
                        >{{ errorMsgPre }}
                          {{
                            Number(
                              $v.interestRatePercent.$params.between.min
                            ).toLocaleString()
                          }}% and
                          {{
                            Number(
                              $v.interestRatePercent.$params.between.max
                            ).toLocaleString()
                          }}%</span>
                      </div>
                    </div>
                  </div>
                  <div
                    id="closing-cost-input-group"
                    class="form-group"
                  >
                    <label for="closing-cost-input-field">Closing Costs</label>
                    <span
                      class="tooltipContent"
                      data-toggle="tooltip"
                      data-placement="right"
                      data-html="true"
                      title="<p>Closing costs are usually 1-2% of loan amount. 1.5% has been used within this example.</p>"
                    >
                      <i class="fa fa-info-circle fa-1x" />
                    </span>
                    <div class="input-group mb-2">
                      <label style="font-size: 18px;font-weight: 700">1.5%</label>
                    </div>
                  </div>
                </div>
              </div>
              <div
                id="button-section"
                class="col"
              >

                <div class="row">
                  <div
                    id="form-mod-group"
                    class="form-group col col-md-6"
                  >
                    <span
                      id="refi-calc-reset-btn"
                      class="text-button button-white fm-btn"
                      onclick="dataLayer.push({
                              'event': 'calculator-cta-link',
                              'calculatorName': 'Refinance calculator',
                              'calculatorCta': 'Reset',
                            });"
                      @click="reset"
                    >
                      Reset
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              id="result"
              class="col-12 col-md-6  chart"
            >
              <div class="sticky">
                <div
                  id="nav-tabContent"
                  class="tab-content"
                >
                  <div
                    id="nav-purchase-info"
                    class="tab-pane fade show active"
                    role="tabpanel"
                    aria-labelledby="nav-purchase-info"
                  >
                    <div class="card-main graph">
                      <div class="card-bottom">
                        <div class="row graph-content">
                          <GraphComponent
                            :values="graphDataValues"
                            :center-text="graphDataCenterText"
                          />
                        </div>
                        <div class="row space-bottom">
                          <label class="col-8">Monthly principal and interest</label>
                          <span class="col-4 right magenta">{{ currencyFormat(monthlyPaymentAndInterest) }}</span>
                        </div>
                        <div class="row space-bottom">
                          <label class="col-8">Monthly taxes, insurance, and HOA, if applicable</label>
                          <span class="col-4 right blue">{{ currencyFormat(monthlyPaymentAndOtherFees) }}</span>
                        </div>
                        <div class="row space-bottom">
                          <label class="col-8">New total monthly payment</label>
                          <span class="col-4 right black">{{ currencyFormat(newTotalMonthlyPayment) }}</span>
                          <small class="payment-text"><i>(Principal, Interest, Taxes, Insurance and HOA fee, if applicable)</i></small>
                        </div>
                        <div class="row space-bottom">
                          <label class="col-8"><b>Potential monthly savings</b></label>
                          <span class="col-4 right black">{{ currencyFormat(monthlySavings) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p class="payment-info">New refinance info</p>
                  <div
                    class="row donut-loan align-center"
                    style="margin:0px;"
                  >
                    <div
                      class="form-group col col-12 amount-head text-center"
                    >  <label class="big mt-2">Potential annual savings:</label>
                      <span class="big mb-2">{{
                        currencyFormat(monthlySavings * 12)
                      }}</span>
                    </div>
                    <div class="col-6 donut-loan__item text-center border-right">
                      <span class="donut-loan__label">Estimated closing costs</span>
                      <span class="donut-loan__value">{{
                        currencyFormat(estClosingCost)
                      }}</span>
                    </div>
                    <div class="col-6 donut-loan__item text-center">
                      <span class="donut-loan__label">Mortgage balance*</span>
                      <span class="donut-loan__value">{{ currencyFormat(mortgageBalance) }} </span>
                    </div>
                  </div>
                  <span class="card-notes"><small><i>*Includes first monthly mortgage balance plus estimated closing costs.</i></small></span>
                </div>
              </div>
            </div>
            <hr>
          </div>
        </form>
      </section>
    </div>
  </div>
</template>

            <script>
import { required, between } from 'vuelidate/lib/validators';
import jQuery from 'jquery';
import GraphComponent from '../components/GraphComponent.vue';
import CurrencyComponent from '../components/Currency.vue';

const $ = jQuery;

const currency = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'currency',
  currency: 'USD',
});

export default {
  name: 'RefinancingCalculator',
  components: {
    GraphComponent,
    CurrencyComponent,
  },
  data() {
    return this.init();
  },
  validations: {
    firstMortgageBalance: {
      required,
      between: between(1000, 5000000),
    },
    estHomeValue: {
      required,
      between: between(1000, 5000000),
    },
    ltvRatio: {
      required,
      between: between(0, 9999999),
    },
    monthlyPayment: {
      required,
      between: between(1, 100000),
    },
    propertyTaxes: {
      required,
      between: between(0, 1000000),
    },
    interestRatePercent: {
      required,
      between: between(0, 20),
    },
    propertyInsurance: {
      required,
      between: between(0, 1000000),
    },
    hoa: {
      required,
      between: between(0, 60000),
    },
    totalMonthlyPayment: {
      required,
      between: between(0, 999999999),
    },
  },
  computed: {
    newTotalMonthlyPayment() {
      return this.monthlyPaymentAndInterest + this.monthlyPaymentAndOtherFees;
    },

    monthlyPaymentAndInterest() {
      let monthlyPmtAndInterest = 0;
      const termInMonths = this.term * 12;
      const principal = this.mortgageBalance;
      if (this.interestRatePercent !== 0) {
        const monthlyInterest = this.interestRatePercent / 1200;
        monthlyPmtAndInterest =
          (principal * monthlyInterest) /
          (1 - (1 / (1 + monthlyInterest)) ** termInMonths);
        return monthlyPmtAndInterest;
      }
      monthlyPmtAndInterest = principal / termInMonths;
      return monthlyPmtAndInterest;
    },
    mortgageBalance() {
      return this.firstMortgageBalance + this.estClosingCost;
    },
    monthlyPaymentAndOtherFees() {
      return this.otherExpenses();
    },
    monthlySavings() {
      return this.totalMonthlyPayment - this.newTotalMonthlyPayment;
    },
    estClosingCost() {
      return (1.5 / 100) * this.firstMortgageBalance;
    },
    graphDataValues() {
      const monthlyPaymentAndInterest = Object();
      monthlyPaymentAndInterest.value = this.monthlyPaymentAndInterest;
      monthlyPaymentAndInterest.color = '#911A58';

      const monthlyPaymentAndOtherFees = Object();
      monthlyPaymentAndOtherFees.value = this.monthlyPaymentAndOtherFees;
      monthlyPaymentAndOtherFees.color = '#0C77BA';

      // eslint-disable-next-line
              const values = Array();
      values[0] = monthlyPaymentAndInterest;
      values[1] = monthlyPaymentAndOtherFees;
      return values;
    },
    graphDataCenterText() {
      // eslint-disable-next-line
              const centerText = Array();
      centerText[0] = 'New total';
      centerText[1] = 'monthly payment';
      centerText[2] = this.newTotalMonthlyPayment;

      return centerText;
    },
  },
  mounted() {
    $('.sliders').tooltip({ selector: '[data-toggle="tooltip"]' });
    // eslint-disable-next-line func-names
    $(document).on('touchstart', '.tooltipContent', function() {
      $(this).tooltip('show');
    });
    // eslint-disable-next-line func-names
    $(document).on('touchend', '.tooltip', function() {
      $(this).tooltip('hide');
    });
  },
  methods: {
    init() {
      return {
        isSubmitted: false,
        firstMortgageBalance: 200000,
        estHomeValue: 400000,
        ltvRatio: 50,
        monthlyPayment: 1000,
        totalMonthlyPayment: 1708.35,
        propertyTaxes: 4000,
        propertyInsurance: 1500,
        hoa: 3000,
        term: 30,
        interestRatePercent: 5,
        errorMsgPre: 'Please enter a valid amount between',
        termRangeStep: 10,
      };
    },
    // eslint-disable-next-line consistent-return
    setSteps() {
      if (this.term < 20) {
        this.termRangeStep = 5;
      } else {
        this.termRangeStep = 10;
      }
    },
    reset() {
      this.firstMortgageBalance = this.init().firstMortgageBalance;
      this.estHomeValue = this.init().estHomeValue;
      this.ltvRatio = this.init().ltvRatio;
      this.monthlyPayment = this.init().monthlyPayment;
      this.totalMonthlyPayment = this.init().totalMonthlyPayment;
      this.propertyTaxes = this.init().propertyTaxes;
      this.propertyInsurance = this.init().propertyInsurance;
      this.hoa = this.init().hoa;
      this.term = this.init().term;
      this.interestRatePercent = this.init().interestRatePercent;
    },
    updateLtvRatio() {
      if (this.firstMortgageBalance != null && this.estHomeValue != null) {
        this.ltvRatio = this.calculateRatio(
          this.firstMortgageBalance,
          this.estHomeValue
        );
      }
      return 105;
    },
    updateTotalMonthlyPayment() {
      let x = 0;
      let y = 0;
      let z = 0;
      let annualCharges = 0;
      let monthlyPmt = 0;
      x = this.propertyInsurance;
      y = this.propertyTaxes;
      z = this.hoa;
      monthlyPmt = this.monthlyPayment;
      if (this.isFieldOptional(x)) {
        x = 0;
      }
      if (this.isFieldOptional(y)) {
        y = 0;
      }
      if (this.isFieldOptional(z)) {
        z = 0;
      }
      if (this.isFieldOptional(monthlyPmt)) {
        monthlyPmt = 0;
      }
      const otherCharges = [x, y, z].reduce((a, b) => a + b, 0);
      annualCharges = otherCharges / 12;
      if (monthlyPmt === 0) {
        this.totalMonthlyPayment = annualCharges;
      }
      this.totalMonthlyPayment =
        annualCharges + parseFloat(this.monthlyPayment);
    },
    focusOnElement() {
      const element = this.$refs['refi-calc-top-of-page'];
      element.scrollIntoView({ behavior: 'smooth' });
    },
    otherExpenses() {
      let monthlyOtherExpense = 0;
      let x = 0;
      let y = 0;
      let z = 0;
      x = this.propertyInsurance;
      y = this.propertyTaxes;
      z = this.hoa;
      if (this.isFieldOptional(x)) {
        x = 0;
      }
      if (this.isFieldOptional(y)) {
        y = 0;
      }
      if (this.isFieldOptional(z)) {
        z = 0;
      }
      const otherCharges = [x, y, z].reduce((a, b) => a + b, 0);
      monthlyOtherExpense = otherCharges / 12;
      return monthlyOtherExpense;
    },
    calculateRatio(x, y) {
      return parseFloat(((x / y) * 100).toFixed(2));
    },
    isFieldOptional(n) {
      return n == null || n === '';
    },
    handleNegativeNums(n) {
      if (n < 0) {
        return 0;
      }
      return n;
    },
    currencyFormat(n) {
      return currency.format(n);
    },
    formatNumber(num) {
      return Number(num).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    sendDataLayer() {
      window.dataLayer.push({
        event: 'calc-interaction',
        calculatorName: 'Refinance calculator',
      });
    },
    sendDataLayerOnBlur(evt) {
      console.log(`value=${evt.target.value}`);
      window.dataLayer.push({
        event: 'calc-interaction',
        calculatorName: 'Refinance calculator',
      });
    },
  },
};
</script>
<style lang="sass">
</style>
